/**
 * Page « BtoB » — ventes aux magasins et revendeurs, saisies à la main.
 *
 * Seule page de pilotage qu'aucune API n'alimente : l'équipe saisit chaque
 * facture. Les KPI se comparent comme partout (période précédente, an
 * dernier) ; les « performances dans le temps » regardent douze mois, parce
 * qu'à quelques factures par mois une période courte ne dit rien.
 */

import type { BtobSale, BtobSaleInput, BtobTotals } from '@shared/types/btob';
import { useState } from 'react';

import { BtobMonthsTable } from '../components/BtobMonthsTable';
import { BtobSaleForm } from '../components/BtobSaleForm';
import { BtobSalesTable } from '../components/BtobSalesTable';
import { BtobScatterChart } from '../components/BtobScatterChart';
import { DonutChart, type DonutSlice } from '../components/DonutChart';
import { SectionShell } from '../components/SectionShell';
import { StatTile, type StatDelta } from '../components/StatTile';
import { useAuth } from '../hooks/useAuth';
import { useBtobMutations, useBtobStats } from '../hooks/useBtob';
import { usePeriodParam } from '../hooks/usePeriodParam';
import { volumeDelta } from '../lib/deltas';
import { formatInteger, formatMoney } from '../lib/format';
import { CATEGORICAL_COLORS, OTHER_COLOR } from '../lib/theme';

const money = (value: number) => formatMoney(value, 'EUR');

export function BtobPage() {
  const { token, signOut } = useAuth();
  const [period] = usePeriodParam();
  const query = useBtobStats({ from: period.from, to: period.to, token, onUnauthorized: signOut });
  const mutations = useBtobMutations();
  const [editing, setEditing] = useState<BtobSale | null>(null);

  const stats = query.data;
  const totals = stats?.comparison.current.totals;
  const overlap = stats?.comparison.comparisonsOverlap ?? false;
  const refs = overlap
    ? [{ label: "l'an dernier", key: 'yearAgo' as const }]
    : [
        { label: 'période préc.', key: 'previous' as const },
        { label: "l'an dernier", key: 'yearAgo' as const },
      ];

  const deltas = (pick: (t: BtobTotals) => number | null, format: (value: number) => string, higherIsBetter: boolean | null = true): StatDelta[] =>
    refs.map(({ label, key }) => {
      const ref = stats?.comparison[key].totals;
      return volumeDelta(
        { label, available: (ref?.invoices ?? 0) > 0 },
        totals ? pick(totals) : null,
        ref ? pick(ref) : null,
        format,
        higherIsBetter,
        'aucune vente',
      );
    });

  const storeSlices: DonutSlice[] = (() => {
    const stores = (stats?.stores ?? []).filter((row) => row.revenueHt > 0);
    const top = stores.slice(0, CATEGORICAL_COLORS.length);
    const others = stores.slice(CATEGORICAL_COLORS.length).reduce((sum, row) => sum + row.revenueHt, 0);
    const slices: DonutSlice[] = top.map((row, index) => ({
      key: row.storeName,
      label: row.storeName,
      value: row.revenueHt,
      color: CATEGORICAL_COLORS[index] ?? OTHER_COLOR,
    }));
    if (others > 0) slices.push({ key: 'others', label: 'Autres magasins', value: Math.round(others * 100) / 100, color: OTHER_COLOR });
    return slices;
  })();

  const handleSubmit = async (input: BtobSaleInput) => {
    if (editing) {
      await mutations.update(editing.id, input);
      setEditing(null);
    } else {
      await mutations.create(input);
    }
  };

  const handleDelete = async (sale: BtobSale) => {
    if (!window.confirm(`Supprimer la vente ${sale.invoiceNumber ?? ''} de ${sale.storeName} (${money(sale.revenueHt)} HT) ?`)) return;
    if (editing?.id === sale.id) setEditing(null);
    await mutations.remove(sale.id);
  };

  return (
    <SectionShell
      lastSyncAt={undefined}
      isPending={query.isPending}
      isFetching={query.isFetching}
      error={query.error}
      hasData={Boolean(stats)}
      title={
        <h1 className="page-title">
          BtoB
          <span className="page-title__sub">Ventes aux magasins et revendeurs, saisies à la main</span>
        </h1>
      }
    >
      <BtobSaleForm
        editing={editing}
        knownStores={stats?.knownStores ?? []}
        isSaving={mutations.isSaving}
        onSubmit={handleSubmit}
        onCancel={() => setEditing(null)}
      />

      <div className="kpi-row">
        <StatTile label="CA HT" value={money(totals?.revenueHt ?? 0)} deltas={deltas((t) => t.revenueHt, money)} hint="par date de facture" />
        <StatTile label="CA TTC" value={money(totals?.revenueTtc ?? 0)} deltas={deltas((t) => t.revenueTtc, money)} hint="selon la TVA de chaque facture" />
        <StatTile label="Unités vendues" value={formatInteger(totals?.units ?? 0)} deltas={deltas((t) => t.units, formatInteger)} />
        <StatTile label="Factures" value={formatInteger(totals?.invoices ?? 0)} deltas={deltas((t) => t.invoices, formatInteger)} />
        <StatTile
          label="Facture moyenne (HT)"
          value={totals?.averageInvoiceHt != null ? formatMoney(totals.averageInvoiceHt, 'EUR', true) : '—'}
          deltas={deltas((t) => t.averageInvoiceHt, (v) => formatMoney(v, 'EUR', true))}
          hint="CA HT ÷ factures"
        />
        <StatTile label="Magasins actifs" value={formatInteger(totals?.stores ?? 0)} deltas={deltas((t) => t.stores, formatInteger)} hint="facturés sur la période" />
        <StatTile
          label="À encaisser (TTC)"
          value={money(totals?.unpaidTtc ?? 0)}
          deltas={deltas((t) => t.unpaidTtc, money, false)}
          hint={`${formatInteger(totals?.unpaidInvoices ?? 0)} facture${(totals?.unpaidInvoices ?? 0) > 1 ? 's' : ''} de la période sans date de paiement, TTC`}
        />
      </div>

      <div className="chart-grid">
        <BtobScatterChart sales={stats?.points ?? []} />
        <DonutChart title="Par magasin (HT)" subtitle="CA HT de la période" slices={storeSlices} formatValue={money} emptyMessage="Aucune vente BtoB sur cette période." />
        <BtobMonthsTable rows={stats?.months ?? []} />
        <BtobSalesTable
          sales={stats?.sales ?? []}
          isSaving={mutations.isSaving}
          onEdit={(sale) => {
            setEditing(sale);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          onDelete={(sale) => void handleDelete(sale)}
        />
      </div>

      <p className="footnote">
        Une vente compte dans la période de sa date de facture, aujourd'hui compris (comparaisons jour pour
        jour) ; une facture datée d'un jour à venir apparaît tout de suite dans la liste, et dans les KPI à sa
        date. Les magasins sont regroupés sans tenir compte des majuscules.
        « À encaisser » : factures de la période sans date de paiement. Le BtoB compte dans le Total et les
        Products du Résumé.
      </p>
    </SectionShell>
  );
}
